import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatDate } from '../../../utils/dateFormat';
import {
  serviceAgentIntroductionService,
  IntroducedInvestorSummary,
} from '../../../services/serviceAgentIntroduction.service';
import { getPermissionErrorMessage } from '../../../utils/apiClient';

/** Investor progress, as far as the platform can see it locally. */
const registrationBadge = (row: IntroducedInvestorSummary) => {
  if (row.status === 'FAILED') return <span className="badge bg-danger">Failed</span>;
  if (row.status === 'DEACTIVATED') return <span className="badge bg-secondary">Deactivated</span>;
  if (row.registrationStatus === 'REGISTERED') return <span className="badge bg-success">Registered</span>;
  return <span className="badge bg-secondary">Mail sent</span>;
};

/**
 * "My Introduced Investors" — everything this Client Executive Agent has introduced.
 *
 * The journey code is created by Power Apps roughly half a minute after the record, so a fresh
 * row shows "creating…" until then. The page refreshes itself a few times to pick it up rather
 * than making the agent reload.
 */
export const IntroducedInvestors: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const justCreated = (location.state as any)?.justCreated as number | undefined;

  const [rows, setRows] = useState<IntroducedInvestorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      setRows(await serviceAgentIntroductionService.getMyIntroductions());
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      toast.error(permissionError || err?.response?.data?.message || 'Failed to load introductions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
  }, []);

  // Poll briefly while any journey is still being generated by Power Apps.
  useEffect(() => {
    const pending = rows.some(r => r.status === 'CREATED' && !r.journeyCode);
    if (!pending) return;
    const timer = window.setTimeout(() => load(), 10000);
    return () => window.clearTimeout(timer);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = search.trim().toLowerCase();
  const filtered = !q ? rows : rows.filter(r =>
    (r.investorName || '').toLowerCase().includes(q) ||
    (r.email || '').toLowerCase().includes(q) ||
    (r.code || '').toLowerCase().includes(q));


  const failed = rows.filter(r => r.status === 'FAILED');

  return (
    <div className="sa-introduced facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-3 px-4 py-3 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">My Introduced Investors</h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">
              Introductions you created, and how far each investor has got.
            </p>
          </div>
          <button
            onClick={() => navigate('/service-agent/introduce-investor')}
            className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors"
          >
            <i className="bi bi-person-plus me-1"></i> Introduce Investor
          </button>
        </div>

        {failed.length > 0 && (
          <div className="alert alert-warning py-2 px-3 text-[12px] mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {failed.length === 1 ? 'One introduction' : `${failed.length} introductions`} could not be saved to
            Power Apps. Nothing was created for {failed.length === 1 ? 'it' : 'them'}, so you can submit the same
            details again.
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] flex flex-col">
          <div className="p-2 flex justify-between items-center border-b border-[#e2e8f0] flex-wrap gap-2">
            <h2 className="text-[14px] font-bold text-slate-800 flex items-center tracking-tight mb-0">
              <i className="bi bi-person-plus mr-2 text-slate-500"></i> Introductions{loading ? '' : ` (${rows.length})`}
            </h2>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              placeholder="Search by name, email, code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="py-5 text-center"><Spinner animation="border" variant="primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-5 text-center text-[12px] text-slate-400">
              {rows.length === 0
                ? 'You have not introduced any investors yet.'
                : 'No introductions match your search.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr className="text-[10px] text-slate-500 text-uppercase">
                    <th>Investor</th>
                    <th>Type</th>
                    <th>Product</th>
                    <th>Brokerage Plan</th>
                    <th>Code</th>
                    <th>Journey</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className={justCreated === r.id ? 'table-warning' : undefined}>
                      <td>
                        <div className="fw-semibold text-slate-800">{r.investorName}</div>
                        <small className="text-muted">{r.email}</small>
                      </td>
                      <td><small className="text-muted">{r.investorType || '—'}</small></td>
                      <td><small>{r.productName || '—'}</small></td>
                      <td><small>{r.planName || '—'}</small></td>
                      <td><small className="text-muted">{r.code || '—'}</small></td>
                      <td>
                        {r.status === 'FAILED'
                          ? <small className="text-muted">—</small>
                          : r.journeyCode
                            ? <small className="text-muted">{r.journeyCode}</small>
                            : <small className="fst-italic text-[#b45309]">creating…</small>}
                      </td>
                      <td><small className="text-muted">{r.createdAt ? formatDate(r.createdAt) : '—'}</small></td>
                      <td>
                        {registrationBadge(r)}
                        {r.status === 'FAILED' && r.lastError && (
                          <div className="text-[10px] text-danger mt-1" style={{ maxWidth: 220 }}>{r.lastError}</div>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/service-agent/introduced-investors/${r.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IntroducedInvestors;
